import { composeShader } from '../../shader/shader-composer.js';
import { createPbrTemplate } from './pbr-template.js';
import { PBR_HAS_EMISSIVE, PBR_HAS_NORMAL_MAP, PBR2_HAS_UV2, PBR2_HAS_UV_TRANSFORM, PBR_HAS_GAMMA_ALBEDO, PBR_HAS_SKYBOX, PBR_HAS_SPECULAR_AA, PBR_HAS_ENV, PBR_HAS_ANISOTROPY, PBR_HAS_SPEC_GLOSS, PBR2_ESM_SHADOW_OUTPUT, PBR2_NO_COLOR_OUTPUT, PBR_HAS_OCCLUSION, PBR2_HAS_BASE_COLOR_FACTOR, PBR_HAS_ALPHA_BLEND, PBR_HAS_FOG, PBR_HAS_TONEMAP, PBR_HAS_DOUBLE_SIDED, PBR_HAS_EMISSIVE_COLOR, PBR_HAS_METALLIC_REFLECTANCE_MAP, PBR_HAS_REFLECTANCE_MAP, PBR2_HAS_REFLECTANCE_FACTORS } from './pbr-flag-bits.js';
import { _getPbrExts } from './pbr-flags.js';
import { MSH_HAS_TANGENTS, MSH_RECEIVE_SHADOWS, MSH_HAS_THIN_INSTANCES, MSH_HAS_VERTEX_COLOR, MSH_HAS_UV2, MSH_FLAT_NORMAL, MSH_HAS_INSTANCE_COLOR, MSH_HAS_MORPH_TARGETS } from '../mesh-features.js';

let _extensionIblResolver = null;
function _installPbrExtensionIblResolver(resolve) {
  _extensionIblResolver = resolve;
}
function createPbrComposer(deps) {
  const cache = /* @__PURE__ */ new Map();
  const {
    _singleLightWGSL,
    _getSingleLightBlock,
    _multiLightWGSL,
    _multiLightLoop,
    _tm,
    _fogHelper,
    _fogBlock,
    _createPbrTemplateExt,
    _flatNormalWgsl,
    _createPbrShadowFragment,
    _shadowLights,
    _createThinInstanceFragment
  } = deps;
  return function composePbr(features, features2 = 0, meshFeatures = 0, sceneFeatures = 0, lightMode = 0, singleLightType = "", _esmShadowDepthCode = "", vbStrides, vbKey = "", uv2Mask = 0, pluginIndex = 0) {
    const ckey = `${features}:${features2}:${meshFeatures}:${sceneFeatures}:${lightMode}:${singleLightType}${vbKey}:${uv2Mask}:${pluginIndex}`;
    const cached = cache.get(ckey);
    if (cached) {
      return cached;
    }
    const has = (bit) => (features & bit) !== 0;
    const hasMesh = (bit) => (meshFeatures & bit) !== 0;
    const hasScene = (bit) => (sceneFeatures & bit) !== 0;
    const hasNormal = has(PBR_HAS_NORMAL_MAP) && hasMesh(MSH_HAS_TANGENTS);
    const hasCotangent = has(PBR_HAS_NORMAL_MAP) && !hasMesh(MSH_HAS_TANGENTS);
    const _hasAnyNormal = hasNormal || hasCotangent;
    const _hasReflectanceExt = has(PBR_HAS_METALLIC_REFLECTANCE_MAP | PBR_HAS_REFLECTANCE_MAP) || (features2 & PBR2_HAS_REFLECTANCE_FACTORS) !== 0;
    const extensionIbl = _extensionIblResolver?.(features, features2) ?? false;
    const _hasIbl = hasScene(PBR_HAS_ENV) || extensionIbl;
    const _hasMorph = hasMesh(MSH_HAS_MORPH_TARGETS);
    const hasShadow = hasMesh(MSH_RECEIVE_SHADOWS);
    const _hasAnisotropy = has(PBR_HAS_ANISOTROPY);
    const _anisoHooks = _hasAnisotropy ? _getPbrExts().get("anisotropy") : void 0;
    const _gammaHooks = has(PBR_HAS_GAMMA_ALBEDO) ? _getPbrExts().get("gamma-albedo") : void 0;
    const _skyboxHooks = has(PBR_HAS_SKYBOX) ? _getPbrExts().get("skybox") : void 0;
    const _hasEmissiveColor = has(PBR_HAS_EMISSIVE_COLOR);
    const _hasEmissiveTexture = has(PBR_HAS_EMISSIVE);
    const hasTI = hasMesh(MSH_HAS_THIN_INSTANCES);
    const _hasUvTransform = (features2 & PBR2_HAS_UV_TRANSFORM) !== 0;
    const _hasVertexColor = hasMesh(MSH_HAS_VERTEX_COLOR);
    const _hasUv2 = (features2 & PBR2_HAS_UV2) !== 0 && hasMesh(MSH_HAS_UV2);
    const needsExt = _hasUvTransform || _hasVertexColor || _hasUv2;
    const _hasSpecularAA = has(PBR_HAS_SPECULAR_AA);
    const _ext = needsExt && _createPbrTemplateExt ? _createPbrTemplateExt({
      _hasUvTransform,
      _hasVertexColor,
      _hasUv2,
      _uv2Mask: uv2Mask,
      _features2: features2,
      _hasAnyNormal,
      _hasEmissiveTexture,
      _hasSpecGloss: has(PBR_HAS_SPEC_GLOSS)
    }) : void 0;
    const template = createPbrTemplate({
      _hasSingleLight: lightMode === 1,
      _hasMultiLight: lightMode === 2,
      _singleLightWGSL,
      _singleLightBlock: lightMode === 1 && _getSingleLightBlock ? _getSingleLightBlock(singleLightType) : "",
      _multiLightWGSL,
      _multiLightLoop,
      _normalMode: hasNormal ? "tangent" : hasCotangent ? "cotangent" : "none",
      _flatGeometricNormal: !_hasAnyNormal && hasMesh(MSH_FLAT_NORMAL),
      _flatNormalWgsl,
      _hasEmissiveTexture,
      _hasSpecGloss: has(PBR_HAS_SPEC_GLOSS),
      _hasDoubleSided: has(PBR_HAS_DOUBLE_SIDED),
      _hasTonemap: hasScene(PBR_HAS_TONEMAP),
      _fogHelper: hasScene(PBR_HAS_FOG) ? _fogHelper : "",
      _fogBlock: hasScene(PBR_HAS_FOG) ? _fogBlock : "",
      _toneMappingHelpers: _tm?.helpersWGSL,
      _toneMappingCall: _tm?.callWGSL,
      _hasAlphaBlend: has(PBR_HAS_ALPHA_BLEND),
      _hasSpecularAA,
      _hasGammaAlbedo: has(PBR_HAS_GAMMA_ALBEDO),
      _hasBaseColorFactor: (features2 & PBR2_HAS_BASE_COLOR_FACTOR) !== 0,
      _hasMorph,
      _hasOcclusion: has(PBR_HAS_OCCLUSION) && !_hasReflectanceExt,
      _hasEmissiveColor,
      _hasReflectanceExt,
      _hasIbl,
      _hasAnisotropy,
      _anisoBrdfFunctions: _anisoHooks?._anisoBrdf,
      _anisoTBBlock: _anisoHooks?._anisoTB(hasNormal, (features2 & _anisoHooks._anisoTexBit) !== 0),
      _ext,
      _gammaBaseColor: _gammaHooks?._gammaBaseColor,
      _noColorOutput: (features2 & PBR2_NO_COLOR_OUTPUT) !== 0,
      _esmShadowOutput: (features2 & PBR2_ESM_SHADOW_OUTPUT) !== 0,
      _esmShadowDepthCode,
      _vbStrides: vbStrides
    });
    const frags = [];
    const fragCtx = {
      _features: features,
      _features2: features2,
      _pi: pluginIndex,
      _meshFeatures: meshFeatures,
      _uv2Mask: _hasUv2 ? uv2Mask : 0,
      _hasIbl,
      _hasAnyNormal,
      _hasSpecularAA,
      _anisoBentNormalCode: _anisoHooks?._anisoBentNormal,
      _iblSkyboxCalc: _skyboxHooks?._skyboxCalc
    };
    if (extensionIbl) {
      fragCtx._hasSceneIbl = hasScene(PBR_HAS_ENV);
    }
    for (const regExt of _getPbrExts().values()) {
      if (regExt.frag) {
        const fr = regExt.frag(fragCtx);
        if (fr) {
          frags.push(fr);
        }
      }
    }
    if (hasShadow && _createPbrShadowFragment) {
      const slots = _shadowLights.map((sl) => ({ lightIndex: sl.lightIndex, shadowType: sl.shadowType }));
      frags.push(_createPbrShadowFragment(slots));
    }
    if (hasTI && _createThinInstanceFragment) {
      frags.push(_createThinInstanceFragment(hasMesh(MSH_HAS_INSTANCE_COLOR)));
    }
    const composed = frags.reduce((shader, fragment) => fragment._pc?.(shader) ?? shader, composeShader(template, frags));
    cache.set(ckey, composed);
    return composed;
  };
}

export { _installPbrExtensionIblResolver, createPbrComposer };
//# sourceMappingURL=pbr-compose.js.map
